/**
 * Regras de apresentação da aba Inadimplência.
 * Espelham `src/lib/inadimplenciaRegras.ts` para o painel filtrar no cliente.
 */

export const CONDICOES_INADIMPLENCIA = ["PP", "PM", "PMM"] as const;
export const INADIMPLENTE_DIAS_MIN = 5;

export type AgingFaixa = "1-30" | "31-60" | "61-90" | "90+";
export type CortePeriodo =
  | "mes_atual"
  | "mes_anterior"
  | "ano_atual"
  | "anos_anteriores"
  | "geral";
export type ReguaEstagio =
  | "lembrete"
  | "administrativa"
  | "extrajudicial"
  | "negativacao_spc"
  | "protesto_juridico";
export type SpcStatus =
  | "nao_consultado"
  | "sem_restricao"
  | "negativado_por_nos"
  | "negativado_por_outros";

export const REGUA_LABEL: Record<ReguaEstagio, string> = {
  lembrete: "Antes do vencimento — lembrete",
  administrativa: "1º mês — cobrança administrativa",
  extrajudicial: "2º mês — cobrança extrajudicial",
  negativacao_spc: "3º mês — negativação SPC",
  protesto_juridico: "4º mês+ — protesto / jurídico",
};

export const SPC_LABEL: Record<SpcStatus, string> = {
  nao_consultado: "Não consultado",
  sem_restricao: "Sem restrição",
  negativado_por_nos: "Negativado por nós",
  negativado_por_outros: "Negativado por outros",
};

export const AGING_ORDEM: AgingFaixa[] = ["1-30", "31-60", "61-90", "90+"];

export const CORTE_ORDEM: CortePeriodo[] = [
  "mes_atual",
  "mes_anterior",
  "ano_atual",
  "anos_anteriores",
  "geral",
];

export const CORTE_LABEL: Record<CortePeriodo, string> = {
  mes_atual: "Mês atual",
  mes_anterior: "Mês anterior",
  ano_atual: "Ano atual",
  anos_anteriores: "Anos anteriores",
  geral: "Geral",
};

export function toYmd(value: string): string {
  const s = String(value ?? "").trim();
  return s.length >= 10 ? s.slice(0, 10) : "";
}

export function lastDayOfMonth(year: number, month1to12: number): string {
  const dt = new Date(Date.UTC(year, month1to12, 0, 12, 0, 0));
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function firstDayOfMonth(year: number, month1to12: number): string {
  return `${year}-${String(month1to12).padStart(2, "0")}-01`;
}

export function cutDateFromYearMonth(yearMonth: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(yearMonth.trim());
  if (!m) return "";
  return lastDayOfMonth(Number(m[1]), Number(m[2]));
}

export function currentYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export function defaultInadimplenciaPeriod(): { startDate: string; endDate: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  return {
    startDate: firstDayOfMonth(y, m),
    endDate: lastDayOfMonth(y, m),
  };
}

export function taxa(numerador: number, denominador: number): number {
  if (!(denominador > 0)) return 0;
  return numerador / denominador;
}

export function formatAnoMes(anoMes: string): string {
  const [y, m] = anoMes.split("-");
  const nomes = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  const idx = Number(m) - 1;
  return `${nomes[idx] ?? m}/${y}`;
}

export function formatTaxaPct(value: number): string {
  return `${(value * 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

export function agingFaixa(atrasoDias: number): AgingFaixa | null {
  if (atrasoDias < 1) return null;
  if (atrasoDias <= 30) return "1-30";
  if (atrasoDias <= 60) return "31-60";
  if (atrasoDias <= 90) return "61-90";
  return "90+";
}

export function bloqueiaNovoCredito(estagio: ReguaEstagio): boolean {
  return estagio !== "lembrete";
}

function prevYearMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
}

export function dueDateInCorte(dueDate: string, cutDate: string, corte: CortePeriodo): boolean {
  const due = toYmd(dueDate);
  const cut = toYmd(cutDate);
  if (!due || !cut) return false;
  const dYm = due.slice(0, 7);
  const cYm = cut.slice(0, 7);
  if (corte === "geral") return true;
  if (corte === "mes_atual") return dYm === cYm;
  if (corte === "mes_anterior") return dYm === prevYearMonth(cYm);
  if (corte === "ano_atual") return due.slice(0, 4) === cut.slice(0, 4);
  return due.slice(0, 4) < cut.slice(0, 4);
}
